import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  StageConfig,
  StageConfigOverride,
  useGetStageConfig,
  useResetUserStageConfig,
  useUpdateGlobalStageConfig,
  useUpdateUserStageConfig,
} from "../services/ProposalGenerationConfigService";
import { useGetAllPersonasWithDependencies } from "src/views/hubSettings/services/PersonaDependencyService";
import { NucToast } from "src/reusable/components/Toast";
import { usePermission } from "src/helpers/Auth";

type StageConfigPanelProps = {
  open: boolean;
  onClose: () => void;
};

const PROMPT_PLACEHOLDER = "{review_report_content}";

type PersonaOption = { id: number; name: string };

const STAGE_CONFIG_KEYS: (keyof StageConfig)[] = [
  "enabled",
  "button_label",
  "button_loading_label",
  "button_done_label",
  "extraction_prompt",
  "stage_noun_singular",
  "stage_noun_plural",
  "report_type_prefix",
  "stage_report_suffix_label",
  "persona_fk",
];

/**
 * Return only the fields of `draft` that differ from `baseline`. A PUT replaces
 * a scope's override with exactly what's sent, so submitting only the diff keeps
 * the override minimal — unspecified fields keep tracking the lower layer
 * (defaults for the global scope, global/defaults for the user scope) rather
 * than freezing the current values in.
 */
const diffConfig = (
  draft: StageConfig,
  baseline: StageConfig,
): StageConfigOverride => {
  const out: StageConfigOverride = {};
  for (const key of STAGE_CONFIG_KEYS) {
    if (draft[key] !== baseline[key]) {
      (out as Record<string, unknown>)[key] = draft[key];
    }
  }
  return out;
};

/** Editable form for one config scope (global or user). */
const ConfigForm = ({
  initial,
  baseline,
  personas,
  saving,
  onSave,
  onReset,
  resetting,
  scopeLabel,
  savePermission,
}: {
  initial: StageConfig;
  baseline: StageConfig;
  personas: PersonaOption[];
  saving: boolean;
  onSave: (payload: StageConfigOverride) => void;
  onReset?: () => void;
  resetting?: boolean;
  scopeLabel: string;
  /** Capability required for both Save and Reset in this scope. */
  savePermission: string;
}) => {
  const [draft, setDraft] = useState<StageConfig>(initial);
  const { canIt: canSave } = usePermission(savePermission);

  useEffect(() => {
    setDraft(initial);
  }, [initial]);

  const promptMissingPlaceholder =
    !draft.extraction_prompt.includes(PROMPT_PLACEHOLDER);

  const set = <K extends keyof StageConfig>(key: K, value: StageConfig[K]) =>
    setDraft((d) => ({ ...d, [key]: value }));

  return (
    <Stack spacing={2} sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary">
        {scopeLabel}
      </Typography>

      <FormControlLabel
        control={
          <Switch
            checked={draft.enabled}
            onChange={(e) => set("enabled", e.target.checked)}
          />
        }
        label="Show the identify-stages button"
      />

      <TextField
        label="Button label"
        size="small"
        fullWidth
        value={draft.button_label}
        onChange={(e) => set("button_label", e.target.value)}
      />
      <TextField
        label="Button label while running"
        size="small"
        fullWidth
        value={draft.button_loading_label}
        onChange={(e) => set("button_loading_label", e.target.value)}
      />
      <TextField
        label="Button label when stages exist"
        size="small"
        fullWidth
        value={draft.button_done_label}
        onChange={(e) => set("button_done_label", e.target.value)}
      />

      <Stack direction="row" spacing={2}>
        <TextField
          label="Stage noun (singular)"
          size="small"
          fullWidth
          value={draft.stage_noun_singular}
          onChange={(e) => set("stage_noun_singular", e.target.value)}
        />
        <TextField
          label="Stage noun (plural)"
          size="small"
          fullWidth
          value={draft.stage_noun_plural}
          onChange={(e) => set("stage_noun_plural", e.target.value)}
        />
      </Stack>

      <TextField
        label="Report type prefix"
        size="small"
        fullWidth
        helperText="Stamped on generated stages (e.g. discipline_report_). Existing stages keep working."
        value={draft.report_type_prefix}
        onChange={(e) => set("report_type_prefix", e.target.value)}
      />
      <TextField
        label="Stage display suffix"
        size="small"
        fullWidth
        helperText='Shown after the stage name, e.g. "Structural Engineering Discipline Report".'
        value={draft.stage_report_suffix_label}
        onChange={(e) => set("stage_report_suffix_label", e.target.value)}
      />

      <TextField
        select
        label="Persona for generated stages"
        size="small"
        fullWidth
        helperText="Overrides the Stage persona slot binding. 'Inherit' falls back to the slot."
        value={draft.persona_fk ?? ""}
        onChange={(e) =>
          set(
            "persona_fk",
            e.target.value === "" ? null : Number(e.target.value),
          )
        }
      >
        <MenuItem value="">Inherit (use slot binding)</MenuItem>
        {personas.map((p) => (
          <MenuItem key={p.id} value={p.id}>
            {p.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        label="Extraction prompt"
        size="small"
        fullWidth
        multiline
        minRows={6}
        value={draft.extraction_prompt}
        onChange={(e) => set("extraction_prompt", e.target.value)}
        error={promptMissingPlaceholder}
        helperText={`Must contain the ${PROMPT_PLACEHOLDER} placeholder.`}
      />

      <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
        {onReset && (
          <Tooltip
            title={
              canSave ? "" : `User doesn't have permissions ${savePermission}`
            }
          >
            <span>
              <Button
                color="inherit"
                onClick={onReset}
                disabled={resetting || saving || !canSave}
              >
                {resetting ? "Resetting..." : "Reset to global"}
              </Button>
            </span>
          </Tooltip>
        )}
        <Tooltip
          title={
            canSave ? "" : `User doesn't have permissions ${savePermission}`
          }
        >
          <span>
            <Button
              variant="contained"
              onClick={() => onSave(diffConfig(draft, baseline))}
              disabled={saving || promptMissingPlaceholder || !canSave}
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          </span>
        </Tooltip>
      </Box>
    </Stack>
  );
};

export const StageConfigPanel = ({ open, onClose }: StageConfigPanelProps) => {
  const { data: config, isLoading } = useGetStageConfig();
  const { data: personasData } = useGetAllPersonasWithDependencies();
  const updateGlobal = useUpdateGlobalStageConfig();
  const updateUser = useUpdateUserStageConfig();
  const resetUser = useResetUserStageConfig();

  const [tab, setTab] = useState(0);

  // Non-admins only get the "My override" tab.
  useEffect(() => {
    if (config?.is_admin === false) setTab(1);
  }, [config?.is_admin]);

  const personas: PersonaOption[] = useMemo(
    () =>
      Array.isArray(personasData?.personas)
        ? personasData.personas.map((p: any) => ({ id: p.id, name: p.name }))
        : [],
    [personasData],
  );

  // Global form is seeded from the global-only config (defaults <- global, no
  // user layer) so the admin never sees or overwrites their personal override.
  // The user form is seeded from the full effective config (what they see today).
  const globalInitial: StageConfig | null = useMemo(
    () => config?.global_effective ?? null,
    [config],
  );

  const handleSaveGlobal = (payload: StageConfigOverride) => {
    updateGlobal.mutate(payload, {
      onSuccess: () => {
        NucToast({ message: "Global config saved", type: "success" });
      },
      onError: () => {
        NucToast({ message: "Failed to save global config", type: "error" });
      },
    });
  };

  const handleSaveUser = (payload: StageConfigOverride) => {
    updateUser.mutate(payload, {
      onSuccess: () => {
        NucToast({ message: "Your override saved", type: "success" });
      },
      onError: () => {
        NucToast({ message: "Failed to save override", type: "error" });
      },
    });
  };

  const handleResetUser = () => {
    resetUser.mutate(undefined, {
      onSuccess: () => {
        NucToast({ message: "Override reset to global", type: "success" });
      },
      onError: () => {
        NucToast({ message: "Failed to reset override", type: "error" });
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Proposal Generation - Configuration</DialogTitle>
      <DialogContent dividers>
        {isLoading || !config || !globalInitial ? (
          <Typography variant="body2" color="text.secondary">
            Loading...
          </Typography>
        ) : (
          <>
            {config?.is_admin && (
              <Tabs value={tab} onChange={(_e, v) => setTab(v)}>
                <Tab label="Global (instance)" value={0} />
                <Tab label="My override" value={1} />
              </Tabs>
            )}

            {config?.is_admin && tab === 0 && (
              <ConfigForm
                initial={globalInitial}
                baseline={config.defaults}
                personas={personas}
                saving={updateGlobal.isLoading}
                onSave={handleSaveGlobal}
                scopeLabel="Instance-wide default that applies to everyone (unless they set their own override)."
                savePermission="admin"
              />
            )}

            {(!config?.is_admin || tab === 1) && (
              <>
                {!config?.is_admin && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    These settings override the instance default for your
                    account only.
                  </Alert>
                )}
                <ConfigForm
                  initial={config.effective}
                  baseline={config.global_effective}
                  personas={personas}
                  saving={updateUser.isLoading}
                  onSave={handleSaveUser}
                  onReset={config.user_override ? handleResetUser : undefined}
                  resetting={resetUser.isLoading}
                  scopeLabel="Overrides the instance default for your account only."
                  savePermission="project:write"
                />
              </>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
